import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { FlatRenterMappingService } from '../../../core/services';
import { CommonDropdownComponent } from '../../../core/ui/common-dropdown/common-dropdown.component';
import { DropdownInfoConfig, DropdownOptionLike } from '../../../core/constants/api-endpoints';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

export interface AssignSaveEvent {
  mode: 'create' | 'edit';
  refId?: number;
}

@Component({
  selector: 'app-new-edit-assign',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonDropdownComponent, MatDatepickerModule, MatInputModule, MatNativeDateModule, TranslatePipe],
  templateUrl: './new-edit-assign.component.html',
  styleUrls: ['./new-edit-assign.component.css', './../../../../CSSFiles/BTN_Style.css']
})
export class NewEditAssignComponent implements OnInit, OnChanges {
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialMapping: Record<string, any> | null = null;
  @Input() preselectedRenter: Record<string, any> | null = null;
  @Input() preselectedFlat: Record<string, any> | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<AssignSaveEvent>();
  @Output() editDocuments = new EventEmitter<void>();

  houseFilterDropdownObj: Partial<DropdownInfoConfig> = {
    apiInfo: 'HOUSE_LIST',
    status: 1,
    name: 'houseFilterId',
    valueKey: 'id',
    display1: 'houseName',
    display2: 'houseNo',
    label: 'assign.houseOptional',
    placeholder: 'placeholder.selectHouse',
    isSearchable: true
  };

  floorFilterDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'floorFilterName',
    valueKey: 'value',
    display1: 'label',
    label: 'assign.floorOptional',
    placeholder: 'placeholder.selectFloor',
    options: [],
    isSearchable: true
  };

  flatDropdownObj: Partial<DropdownInfoConfig> = {
    apiInfo: 'FLAT_LIST',
    status: 1,
    name: 'flatId',
    valueKey: 'id',
    display1: 'flatName',
    label: 'assign.flat',
    placeholder: 'placeholder.selectFlat',
    isSearchable: true
  };

  renterDropdownObj: Partial<DropdownInfoConfig> = {
    apiInfo: 'RENTER_LIST',
    status: 1,
    name: 'renterId',
    valueKey: 'id',
    display1: 'renterName',
    display2: 'renterMobile',
    label: 'assign.renter',
    placeholder: 'placeholder.selectRenter',
    isSearchable: true
  };

  assign = {
    flatId: null as number | null,
    renterId: null as number | null,
    advanceAmount: 0 as number | null,
    renterStartDate: '',
    renterEndDate: '',
    status: 1 as number | null
  };
  matDateValue: Date | null = null;
  matEndDateValue: Date | null = null;
  flatDropdownFilters: Record<string, unknown> = {};
  selectedHouseId: number | null = null;
  selectedFloorName: string | null = null;

  isSubmitting = false;
  error: string | null = null;

  constructor(private readonly mappings: FlatRenterMappingService) {}

  ngOnInit(): void {
    this.assign.renterStartDate = this.getFirstDayOfCurrentMonthDisplay();

    if (this.mode === 'edit' && this.initialMapping) {
      const flatId = Number(this.initialMapping['flat']?.['id'] ?? this.initialMapping['flatId']);
      const renterId = Number(this.initialMapping['renter']?.['id'] ?? this.initialMapping['renterId']);

      this.assign.flatId = Number.isFinite(flatId) ? flatId : null;
      this.assign.renterId = Number.isFinite(renterId) ? renterId : null;
      this.assign.advanceAmount = Number(this.initialMapping['advanceAmount'] ?? 0);
      const rawStatus = Number(this.initialMapping['status']);
      this.assign.status = Number.isFinite(rawStatus) ? rawStatus : 1;
      this.assign.renterStartDate = this.toDisplayDateValue(this.initialMapping['renterStartDate']) || this.getFirstDayOfCurrentMonthDisplay();
      this.assign.renterEndDate = this.toDisplayDateValue(this.initialMapping['renterEndDate']) || '';
    }

    if (this.mode === 'create' && this.preselectedRenter) {
      this.applyPreselectedRenter();
    }

    if (this.mode === 'create' && this.preselectedFlat) {
      this.applyPreselectedFlat();
    }

    this.matDateValue = this.toDateFromDisplay(this.assign.renterStartDate);
    this.matEndDateValue = this.toDateFromDisplay(this.assign.renterEndDate);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['preselectedRenter'] || changes['mode']) && this.mode === 'create' && !this.initialMapping) {
      this.applyPreselectedRenter();
    }
    if ((changes['preselectedFlat'] || changes['mode']) && this.mode === 'create' && !this.initialMapping) {
      this.applyPreselectedFlat();
    }
  }

  closeModal(): void {
    this.close.emit();
  }

  onFlatChange(event: { value: unknown }): void {
    const flatId = Number(event.value);
    this.assign.flatId = Number.isFinite(flatId) ? flatId : null;
  }

  onFlatOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    if (this.assign.flatId !== null && this.assign.flatId !== undefined) {
      return;
    }

    const options = Array.isArray(event?.options) ? event.options : [];
    if (options.length !== 1) {
      return;
    }

    const flatId = Number(options[0]?.['id']);
    this.assign.flatId = Number.isFinite(flatId) ? flatId : null;
  }

  onHouseFilterChange(event: { value: unknown; option?: DropdownOptionLike }): void {
    const houseId = Number(event.value);
    this.selectedHouseId = Number.isFinite(houseId) ? houseId : null;

    const floors = Array.isArray(event.option?.['floors'])
      ? (event.option?.['floors'] as unknown[])
      : [];

    const floorOptions = floors
      .map((item) => `${item ?? ''}`.trim())
      .filter((item) => !!item)
      .map((name) => ({ value: name, label: name }));

    this.floorFilterDropdownObj = {
      ...this.floorFilterDropdownObj,
      options: floorOptions
    };

    this.selectedFloorName = null;
    this.assign.flatId = null;
    this.updateFlatFilters();
  }

  onFloorFilterChange(event: { value: unknown }): void {
    const floor = `${event.value ?? ''}`.trim();
    this.selectedFloorName = floor ? floor : null;
    this.assign.flatId = null;
    this.updateFlatFilters();
  }

  onRenterChange(event: { value: unknown }): void {
    const renterId = Number(event.value);
    this.assign.renterId = Number.isFinite(renterId) ? renterId : null;
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const pickedDate = event.value;
    if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
      return;
    }

    const day = `${pickedDate.getDate()}`.padStart(2, '0');
    const month = `${pickedDate.getMonth() + 1}`.padStart(2, '0');
    const year = pickedDate.getFullYear();
    this.assign.renterStartDate = `${day}-${month}-${year}`;
    this.matDateValue = new Date(year, pickedDate.getMonth(), pickedDate.getDate());
  }

  onEndDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const pickedDate = event.value;
    if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
      return;
    }

    const day = `${pickedDate.getDate()}`.padStart(2, '0');
    const month = `${pickedDate.getMonth() + 1}`.padStart(2, '0');
    const year = pickedDate.getFullYear();
    this.assign.renterEndDate = `${day}-${month}-${year}`;
    this.matEndDateValue = new Date(year, pickedDate.getMonth(), pickedDate.getDate());
  }

  clearEndDate(): void {
    this.assign.renterEndDate = '';
    this.matEndDateValue = null;
  }

  submitForm(): void {
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.mappings.update(payload)
      : this.mappings.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (res: any) => {
          const refId = Number(res?.refId);
          this.save.emit({
            mode: this.mode,
            refId: Number.isFinite(refId) ? refId : undefined
          });
        },
        error: (err: any) => {
          this.error = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} assign mapping.`;
        }
      });
  }

  openDocumentUpdate(): void {
    if (this.mode !== 'edit') {
      return;
    }
    this.editDocuments.emit();
  }

  private buildPayload(): Record<string, any> | null {
    if (!this.assign.flatId) {
      this.error = 'Flat is required.';
      return null;
    }

    if (!this.assign.renterId) {
      this.error = 'Renter is required.';
      return null;
    }

    if (!this.assign.renterStartDate) {
      this.error = 'Renter start date is required.';
      return null;
    }

    const renterStartDate = this.toApiDateValue(this.assign.renterStartDate);
    if (!renterStartDate) {
      this.error = 'Renter start date must be in dd-mm-yyyy format.';
      return null;
    }

    const payload: Record<string, any> = {
      flat: { id: this.assign.flatId },
      renter: { id: this.assign.renterId },
      advanceAmount: Number(this.assign.advanceAmount || 0),
      status: 1,
      renterStartDate
    };

    if (this.assign.advanceAmount !== null && this.assign.advanceAmount < 0) {
      this.error = 'Advance amount cannot be negative.';
      return null;
    }

    if (this.assign.renterEndDate) {
      const renterEndDate = this.toApiDateValue(this.assign.renterEndDate);
      if (!renterEndDate) {
        this.error = 'Renter end date must be in dd-mm-yyyy format.';
        return null;
      }

      payload['renterEndDate'] = renterEndDate;
    }

    if (this.mode === 'edit') {
      const id = Number(this.initialMapping?.['id']);
      if (!Number.isFinite(id)) {
        this.error = 'Mapping id is missing for update.';
        return null;
      }
      payload['id'] = id;
    }

    return payload;
  }

  private toDisplayDateValue(value: unknown): string {
    if (!value) {
      return '';
    }

    const text = `${value}`;
    if (/^\d{2}-\d{2}-\d{4}$/.test(text)) {
      return text;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const [year, month, day] = text.split('-');
      return `${day}-${month}-${year}`;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const day = `${parsed.getDate()}`.padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private toApiDateValue(value: string): string | null {
    const text = (value ?? '').trim();
    const match = text.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
  }

  private getFirstDayOfCurrentMonthDisplay(): string {
    const now = new Date();
    const day = '01';
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const year = now.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private toDateFromDisplay(value: string): Date | null {
    const match = (value ?? '').trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }

    return parsed;
  }

  private updateFlatFilters(): void {
    const filters: Record<string, unknown> = {};

    if (this.selectedHouseId !== null) {
      filters['house.id'] = this.selectedHouseId;
    }

    if (this.selectedFloorName) {
      filters['floorName'] = this.selectedFloorName;
    }

    this.flatDropdownFilters = filters;
  }

  private applyPreselectedRenter(): void {
    const renterId = Number(this.preselectedRenter?.['id']);
    this.assign.renterId = Number.isFinite(renterId) ? renterId : null;
  }

  private applyPreselectedFlat(): void {
    const flatId = Number(this.preselectedFlat?.['id']);
    this.assign.flatId = Number.isFinite(flatId) ? flatId : null;

    const houseId = Number(this.preselectedFlat?.['house']?.['id'] ?? this.preselectedFlat?.['houseId']);
    this.selectedHouseId = Number.isFinite(houseId) ? houseId : null;

    const floorName = `${this.preselectedFlat?.['floorName'] ?? ''}`.trim();
    this.selectedFloorName = floorName || null;
    this.floorFilterDropdownObj = {
      ...this.floorFilterDropdownObj,
      options: this.buildFloorOptionsFromPreselectedFlat(floorName)
    };
    this.updateFlatFilters();
  }

  private buildFloorOptionsFromPreselectedFlat(selectedFloorName: string): Array<{ value: string; label: string }> {
    const floors = Array.isArray(this.preselectedFlat?.['house']?.['floors'])
      ? (this.preselectedFlat?.['house']?.['floors'] as unknown[])
      : [];

    const names = new Set<string>();
    floors
      .map((item) => `${item ?? ''}`.trim())
      .filter((item) => !!item)
      .forEach((item) => names.add(item));

    if (selectedFloorName) {
      names.add(selectedFloorName);
    }

    return Array.from(names).map((name) => ({ value: name, label: name }));
  }
}
